import React, { useState, useEffect, useRef } from 'react';
import {
  Phone, MessageSquare, Settings, RefreshCw, Link, CheckCircle,
  XCircle, Clock, PhoneIncoming, PhoneOutgoing, PhoneMissed,
  BarChart3, Wifi, WifiOff, Key, Shield, Loader2, AlertCircle,
  ExternalLink, Sparkles, Upload, FileJson
} from 'lucide-react';
import api from '../../services/api';

function RingCentralIntegration() {
  const [activeSubTab, setActiveSubTab] = useState('settings');
  const [config, setConfig] = useState(null);
  const [calls, setCalls] = useState([]);
  const [messages, setMessages] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [webrtcStatus, setWebrtcStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [designatedUserId, setDesignatedUserId] = useState(null);
  const [users, setUsers] = useState([]);
  const [routingSaving, setRoutingSaving] = useState(false);
  const [routingSaved, setRoutingSaved] = useState(false);

  // Config form state
  const [configForm, setConfigForm] = useState({
    client_id: '',
    client_secret: '',
    jwt_token: ''
  });

  // File upload ref
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [activeSubTab]);

  // Handle JSON file upload
  const handleJsonUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.name.endsWith('.json')) {
      setError('Please upload a valid JSON file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);

        // Helper to clean string values (strip quotes and whitespace)
        const cleanValue = (val) => {
          if (!val) return '';
          return String(val).replace(/^["']|["']$/g, '').trim();
        };

        // Map common field names to our expected format (ensure all are strings, strip quotes)
        const mappedData = {
          client_id: cleanValue(jsonData.client_id || jsonData.clientId || jsonData.app_key || jsonData.appKey),
          client_secret: cleanValue(jsonData.client_secret || jsonData.clientSecret || jsonData.app_secret || jsonData.appSecret),
          jwt_token: cleanValue(jsonData.jwt_token || jsonData.jwtToken || jsonData.jwt || jsonData.JWT || jsonData.credential)
        };

        // Validate that we have at least some data
        if (!mappedData.client_id && !mappedData.client_secret && !mappedData.jwt_token) {
          setError('JSON file does not contain recognized credentials. Expected fields: client_id, client_secret, jwt_token');
          return;
        }

        setConfigForm(mappedData);
        setSuccess('Credentials loaded from JSON file. Review and click "Save Configuration" to apply.');
        setError('');
      } catch (parseError) {
        setError('Failed to parse JSON file: ' + parseError.message);
      }
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);

    // Reset file input so same file can be uploaded again
    event.target.value = '';
  };

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      // Always load config and WebRTC status
      try {
        const configRes = await api.get('/ringcentral/config');
        setConfig(configRes.data);
      } catch (e) {
        // Config might not exist yet
      }

      try {
        const statusRes = await api.get('/ringcentral/webrtc/status');
        setWebrtcStatus(statusRes.data);
      } catch (e) {
        // Status endpoint might fail
      }

      if (activeSubTab === 'overview' || activeSubTab === 'analytics') {
        try {
          const analyticsRes = await api.get('/ringcentral/analytics');
          setAnalytics(analyticsRes.data);
        } catch (e) {}
      }

      if (activeSubTab === 'calls') {
        try {
          const callsRes = await api.get('/ringcentral/calls?limit=100');
          setCalls(callsRes.data || []);
        } catch (e) {}
      }

      if (activeSubTab === 'messages') {
        try {
          const msgsRes = await api.get('/ringcentral/messages?limit=100');
          setMessages(msgsRes.data || []);
        } catch (e) {}
      }

      if (activeSubTab === 'settings') {
        try {
          const logsRes = await api.get('/ringcentral/sync-logs');
          setSyncLogs(logsRes.data || []);
        } catch (e) {}
        // Fetch firm settings for designated phone user
        api.get('/general')
          .then(r => setDesignatedUserId(r.data.designated_phone_user_id))
          .catch(() => {});
        // Fetch users for dropdown
        api.get('/users/')
          .then(r => setUsers(r.data || []))
          .catch(() => {});
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        setError('Failed to load data: ' + (err.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      // Ensure all values are strings
      const payload = {
        client_id: configForm.client_id || '',
        client_secret: configForm.client_secret || '',
        jwt_token: configForm.jwt_token || ''
      };
      await api.post('/ringcentral/config', payload);
      setSuccess('Configuration saved successfully!');
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
        : (typeof detail === 'string' ? detail : err.message);
      setError('Failed to save config: ' + errorMsg);
    }
  };

  const handleRoutingSave = async () => {
    setRoutingSaving(true);
    try {
      await api.put('/general', {
        designated_phone_user_id: designatedUserId || null
      });
      setRoutingSaved(true);
      setTimeout(() => setRoutingSaved(false), 3000);
    } catch (e) {
      console.error('Failed to save routing:', e);
    } finally {
      setRoutingSaving(false);
    }
  };

  const handleAuthenticate = async () => {
    setError('');
    setSuccess('');
    setAuthenticating(true);
    try {
      await api.post('/ringcentral/auth/token');
      setSuccess('Authentication successful! Phone system is now connected.');
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
        : (typeof detail === 'string' ? detail : err.message);
      setError('Authentication failed: ' + errorMsg);
    } finally {
      setAuthenticating(false);
    }
  };

  const handleSync = async (type = 'calls', daysBack = 7) => {
    setSyncing(true);
    setError('');
    setSuccess('');
    try {
      await api.post(`/api/ringcentral/sync/${type}?days_back=${daysBack}`);
      setSuccess(`Sync started for ${type}. This may take a moment...`);
      setTimeout(() => {
        loadData();
        setSyncing(false);
      }, 3000);
    } catch (err) {
      setError('Sync failed: ' + (err.response?.data?.detail || err.message));
      setSyncing(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getDirectionIcon = (direction, result) => {
    if (result === 'Missed') return <PhoneMissed className="w-4 h-4 text-red-400" />;
    if (direction === 'Inbound') return <PhoneIncoming className="w-4 h-4 text-green-400" />;
    return <PhoneOutgoing className="w-4 h-4 text-blue-400" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center">
            <Phone className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-virgo-gold-400 to-amber-300 bg-clip-text text-transparent">
              Planchette Settings
            </h2>
            <p className="text-virgo-teal-400 text-sm mt-0.5">Configure RingCentral integration for VoIP calling</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {webrtcStatus?.ready ? (
            <span className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
              <Wifi className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm font-medium">Connected</span>
            </span>
          ) : config ? (
            <span className="flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <WifiOff className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 text-sm font-medium">Authentication Required</span>
            </span>
          ) : (
            <span className="flex items-center gap-2 px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg">
              <WifiOff className="w-4 h-4 text-virgo-teal-500" />
              <span className="text-virgo-teal-400 text-sm font-medium">Not Configured</span>
            </span>
          )}
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-3 px-4 py-3 bg-green-500/10 border border-green-500/20 rounded-xl">
          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
          <span className="text-green-400">{success}</span>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="border-b border-virgo-navy-600">
        <nav className="flex space-x-1">
          {[
            { id: 'settings', label: 'Configuration', icon: Key },
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'calls', label: 'Call Logs', icon: Phone },
            { id: 'messages', label: 'Messages', icon: MessageSquare }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`py-3 px-4 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
                activeSubTab === tab.id
                  ? 'border-cyan-500 text-cyan-400'
                  : 'border-transparent text-virgo-teal-400 hover:text-white hover:border-virgo-navy-500'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      ) : (
        <>
          {/* Settings Tab */}
          {activeSubTab === 'settings' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Configuration Form */}
              <div className="bg-virgo-navy-700 rounded-xl border border-virgo-navy-600 overflow-hidden">
                <div className="px-6 py-4 border-b border-virgo-navy-600 bg-virgo-navy-700/50">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <Key className="w-5 h-5 text-cyan-400" />
                    RingCentral API Credentials
                  </h3>
                </div>
                <form onSubmit={handleSaveConfig} className="p-6 space-y-4">
                  {/* JSON Upload Area */}
                  <div
                    className="border-2 border-dashed border-virgo-navy-500 rounded-xl p-6 text-center hover:border-cyan-500/50 transition-colors cursor-pointer group"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const file = e.dataTransfer.files[0];
                      if (file) {
                        const fakeEvent = { target: { files: [file], value: '' } };
                        handleJsonUpload(fakeEvent);
                      }
                    }}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleJsonUpload}
                      accept=".json,application/json"
                      className="hidden"
                    />
                    <FileJson className="w-10 h-10 mx-auto mb-3 text-virgo-navy-400 group-hover:text-cyan-400 transition-colors" />
                    <p className="text-virgo-teal-300 font-medium">Upload JSON Credentials</p>
                    <p className="text-virgo-teal-500 text-sm mt-1">
                      Drag & drop or click to upload your RingCentral credentials file
                    </p>
                    <button
                      type="button"
                      className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-virgo-navy-600 hover:bg-virgo-navy-500 text-virgo-teal-300 rounded-lg text-sm transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      Choose File
                    </button>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-virgo-navy-600"></div>
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-virgo-navy-700 px-3 text-virgo-teal-500">or enter manually</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                      Client ID
                    </label>
                    <input
                      type="text"
                      value={configForm.client_id}
                      onChange={e => setConfigForm({...configForm, client_id: e.target.value})}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-600 rounded-lg px-4 py-3 text-white placeholder-virgo-navy-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      placeholder="Your RingCentral Client ID"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                      Client Secret
                    </label>
                    <input
                      type="password"
                      value={configForm.client_secret}
                      onChange={e => setConfigForm({...configForm, client_secret: e.target.value})}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-600 rounded-lg px-4 py-3 text-white placeholder-virgo-navy-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      placeholder="Your RingCentral Client Secret"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                      JWT Token
                    </label>
                    <input
                      type="password"
                      value={configForm.jwt_token}
                      onChange={e => setConfigForm({...configForm, jwt_token: e.target.value})}
                      className="w-full bg-virgo-navy-800 border border-virgo-navy-600 rounded-lg px-4 py-3 text-white placeholder-virgo-navy-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      placeholder="JWT credential for server-to-server auth"
                    />
                    <p className="text-xs text-virgo-teal-500 mt-2 flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" />
                      Get credentials from the RingCentral Developer Console
                    </p>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      className="flex-1 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white px-4 py-3 rounded-lg hover:from-cyan-600 hover:to-indigo-700 font-medium transition-all shadow-lg shadow-cyan-500/25"
                    >
                      Save Configuration
                    </button>
                    {config && (
                      <button
                        type="button"
                        onClick={handleAuthenticate}
                        disabled={authenticating}
                        className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-green-500/50 text-white px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                      >
                        {authenticating ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Authenticating...
                          </>
                        ) : (
                          <>
                            <Shield className="w-4 h-4" />
                            Test Authentication
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </form>
              </div>

              {/* Status Panel */}
              <div className="space-y-6">
                {/* Connection Status */}
                <div className="bg-virgo-navy-700 rounded-xl border border-virgo-navy-600 p-6">
                  <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                    <Wifi className="w-5 h-5 text-cyan-400" />
                    Connection Status
                  </h3>

                  {config ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between py-2 border-b border-virgo-navy-600">
                        <span className="text-virgo-teal-400">Client ID</span>
                        <span className="text-white font-mono text-sm">{config.client_id?.slice(0, 20)}...</span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-b border-virgo-navy-600">
                        <span className="text-virgo-teal-400">Token Status</span>
                        {config.has_valid_token ? (
                          <span className="flex items-center gap-2 text-green-400">
                            <CheckCircle className="w-4 h-4" />
                            Valid
                          </span>
                        ) : (
                          <span className="flex items-center gap-2 text-red-400">
                            <XCircle className="w-4 h-4" />
                            Invalid/Expired
                          </span>
                        )}
                      </div>
                      {config.token_expires_at && (
                        <div className="flex items-center justify-between py-2 border-b border-virgo-navy-600">
                          <span className="text-virgo-teal-400">Expires</span>
                          <span className="text-white text-sm">{formatDateTime(config.token_expires_at)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between py-2">
                        <span className="text-virgo-teal-400">Last Sync</span>
                        <span className="text-white text-sm">{config.last_sync_at ? formatDateTime(config.last_sync_at) : 'Never'}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <WifiOff className="w-12 h-12 text-virgo-navy-500 mx-auto mb-3" />
                      <p className="text-virgo-teal-400">Not configured yet</p>
                      <p className="text-virgo-teal-500 text-sm mt-1">Enter your RingCentral credentials to get started</p>
                    </div>
                  )}
                </div>

                {/* Sync Actions */}
                {config?.has_valid_token && (
                  <div className="bg-virgo-navy-700 rounded-xl border border-virgo-navy-600 p-6">
                    <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 text-cyan-400" />
                      Sync Call Data
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => handleSync('calls', 7)}
                        disabled={syncing}
                        className="px-4 py-3 bg-virgo-navy-600 hover:bg-virgo-navy-500 text-white rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        Last 7 Days
                      </button>
                      <button
                        onClick={() => handleSync('calls', 30)}
                        disabled={syncing}
                        className="px-4 py-3 bg-virgo-navy-600 hover:bg-virgo-navy-500 text-white rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        Last 30 Days
                      </button>
                    </div>
                  </div>
                )}

                {/* Sync History */}
                {syncLogs.length > 0 && (
                  <div className="bg-virgo-navy-700 rounded-xl border border-virgo-navy-600 p-6">
                    <h3 className="font-semibold text-white mb-4">Recent Syncs</h3>
                    <div className="space-y-2">
                      {syncLogs.slice(0, 5).map(log => (
                        <div key={log.id} className="flex items-center justify-between py-2 border-b border-virgo-navy-600 last:border-0">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${
                              log.status === 'completed' ? 'bg-green-400' :
                              log.status === 'failed' ? 'bg-red-400' : 'bg-yellow-400'
                            }`} />
                            <span className="text-white text-sm">{log.sync_type}</span>
                          </div>
                          <span className="text-virgo-teal-500 text-xs">{log.records_created} records</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Call Routing */}
                <div className="bg-virgo-navy-700 rounded-xl border border-virgo-navy-600 p-6">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Phone className="w-5 h-5 text-cyan-400" />
                    Call Routing
                  </h3>
                  <p className="text-virgo-teal-400 text-sm mb-3">
                    Designate which staff member receives incoming call screen pops. Leave unset to notify all logged-in users.
                  </p>
                  <select
                    value={designatedUserId || ''}
                    onChange={e => setDesignatedUserId(e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full bg-virgo-navy-800 border border-virgo-navy-600 text-white rounded-lg px-4 py-3 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  >
                    <option value="">All logged-in users</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.full_name || u.username} ({u.email})
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleRoutingSave}
                    disabled={routingSaving}
                    className="bg-gradient-to-r from-cyan-500 to-indigo-600 hover:from-cyan-600 hover:to-indigo-700 disabled:opacity-50 text-white px-4 py-3 rounded-lg text-sm font-medium transition-all shadow-lg shadow-cyan-500/25"
                  >
                    {routingSaving ? 'Saving...' : routingSaved ? '\u2713 Saved' : 'Save Routing'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Overview Tab */}
          {activeSubTab === 'overview' && analytics && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-virgo-navy-700 rounded-xl border border-virgo-navy-600 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-virgo-teal-400">Total Calls</p>
                      <p className="text-3xl font-bold text-white">{analytics.total_calls}</p>
                    </div>
                    <Phone className="w-10 h-10 text-cyan-400 opacity-50" />
                  </div>
                </div>

                <div className="bg-virgo-navy-700 rounded-xl border border-virgo-navy-600 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-virgo-teal-400">Calls Today</p>
                      <p className="text-3xl font-bold text-white">{analytics.calls_today}</p>
                    </div>
                    <Clock className="w-10 h-10 text-green-400 opacity-50" />
                  </div>
                </div>

                <div className="bg-virgo-navy-700 rounded-xl border border-virgo-navy-600 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-virgo-teal-400">Missed Calls</p>
                      <p className="text-3xl font-bold text-red-400">{analytics.missed_calls}</p>
                    </div>
                    <PhoneMissed className="w-10 h-10 text-red-400 opacity-50" />
                  </div>
                </div>

                <div className="bg-virgo-navy-700 rounded-xl border border-virgo-navy-600 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-virgo-teal-400">Avg Duration</p>
                      <p className="text-3xl font-bold text-white">{formatDuration(analytics.avg_duration)}</p>
                    </div>
                    <BarChart3 className="w-10 h-10 text-blue-400 opacity-50" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Calls Tab */}
          {activeSubTab === 'calls' && (
            <div className="bg-virgo-navy-700 rounded-xl border border-virgo-navy-600 overflow-hidden">
              {calls.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-xs text-virgo-teal-400 uppercase border-b border-virgo-navy-600">
                      <th className="px-6 py-4">Direction</th>
                      <th className="px-6 py-4">From</th>
                      <th className="px-6 py-4">To</th>
                      <th className="px-6 py-4">Duration</th>
                      <th className="px-6 py-4">Date</th>
                      <th className="px-6 py-4">Linked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calls.map(call => (
                      <tr key={call.id} className="border-b border-virgo-navy-600 hover:bg-virgo-navy-600/50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {getDirectionIcon(call.direction, call.result)}
                            <span className="text-white text-sm">{call.direction}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-white">{call.from_number || '-'}</td>
                        <td className="px-6 py-4 text-white">{call.to_number || '-'}</td>
                        <td className="px-6 py-4 text-virgo-teal-300">{formatDuration(call.duration)}</td>
                        <td className="px-6 py-4 text-virgo-teal-400 text-sm">{formatDateTime(call.start_time)}</td>
                        <td className="px-6 py-4">
                          {call.contact_id || call.case_id ? (
                            <span className="flex items-center gap-1 text-green-400">
                              <Link className="w-4 h-4" />
                            </span>
                          ) : (
                            <span className="text-virgo-navy-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-12 text-center">
                  <Phone className="w-12 h-12 text-virgo-navy-500 mx-auto mb-3" />
                  <p className="text-virgo-teal-400">No calls synced yet</p>
                  <p className="text-virgo-teal-500 text-sm mt-1">Sync your call logs from the Configuration tab</p>
                </div>
              )}
            </div>
          )}

          {/* Messages Tab */}
          {activeSubTab === 'messages' && (
            <div className="bg-virgo-navy-700 rounded-xl border border-virgo-navy-600 overflow-hidden">
              {messages.length > 0 ? (
                <div className="divide-y divide-virgo-navy-600">
                  {messages.map(msg => (
                    <div key={msg.id} className="p-4 hover:bg-virgo-navy-600/50">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            msg.direction === 'Inbound' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {msg.direction}
                          </span>
                          <span className="text-sm text-virgo-teal-300">
                            {msg.from_number} → {msg.to_number}
                          </span>
                        </div>
                        <span className="text-xs text-virgo-teal-500">{formatDateTime(msg.sent_at)}</span>
                      </div>
                      {msg.message_text && (
                        <p className="text-white text-sm">{msg.message_text}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-virgo-navy-500 mx-auto mb-3" />
                  <p className="text-virgo-teal-400">No messages synced yet</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default RingCentralIntegration;
